import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { JobHttpService } from 'cms/features/feature_job/service';
import SalarySpecificationForm from '../components/SalarySpecificationForm';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Divider,
    IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';

function SalarySpecificationDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [editFormOpen, setEditFormOpen] = useState(false);
    const [editingField, setEditingField] = useState(null);

    const { data: specification, isLoading, isError, error } = useQuery(
        ['salarySpecification', id],
        () => JobHttpService.loadSalarySpecification(id),
        { enabled: !!id }
    );

    const handleBack = () => {
        navigate('/employer/salary-specification');
    };

    const handleEditField = (field) => {
        setEditingField(field);
        setEditFormOpen(true);
    };

    const handleFormClose = () => {
        setEditFormOpen(false);
        setEditingField(null);
    };

    const handleSpecificationSaved = () => {
        queryClient.invalidateQueries(['salarySpecification', id]);
        queryClient.invalidateQueries(['salarySpecifications']);
        setEditFormOpen(false);
        setEditingField(null);
    };

    const handleDownload = async () => {
        try {
            const blob = await JobHttpService.generateSalarySpecificationPdf(id);
            
            // Check if blob is valid
            if (!blob || blob.type !== 'application/pdf') {
                throw new Error('Invalid PDF response');
            }
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `salary-specification-${id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading PDF:', err);
            alert('Failed to download PDF. Please try again.');
        }
    };

    if (isLoading) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </section>
        );
    }

    if (isError || !specification) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Alert severity="error">
                        {error?.message || 'Failed to load salary specification'}
                    </Alert>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        sx={{ mt: 2 }}
                    >
                        Back to Salary Specifications
                    </Button>
                </Container>
            </section>
        );
    }

    const employee = specification.employee;
    const employeeName = employee 
        ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
        : 'Unknown Employee';

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <section className="section-box mt-50">
            <Container maxWidth="lg">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{ mb: 3 }}
                >
                    Back
                </Button>

                {/* Employee and Key Dates Card */}
                <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <AccountBalanceIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                                <Box>
                                    <Typography variant="h5" fontWeight={600} gutterBottom>
                                        {employeeName}
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton
                                onClick={() => handleEditField('all')}
                                color="primary"
                            >
                                <EditIcon />
                            </IconButton>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* Salary Period Start */}
                        <ListItemButton
                            onClick={() => handleEditField('salary_period_start')}
                            sx={{ px: 0, py: 1.5 }}
                        >
                            <ListItemIcon>
                                <CalendarTodayIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Salary period start"
                                secondary={formatDate(specification.salary_period_start)}
                            />
                            <ArrowForwardIcon color="action" />
                        </ListItemButton>

                        {/* Salary Period End */}
                        <ListItemButton
                            onClick={() => handleEditField('salary_period_end')}
                            sx={{ px: 0, py: 1.5 }}
                        >
                            <ListItemIcon>
                                <CalendarTodayIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Salary period end"
                                secondary={formatDate(specification.salary_period_end)}
                            />
                            <ArrowForwardIcon color="action" />
                        </ListItemButton>

                        {/* Payment Date */}
                        <ListItemButton
                            onClick={() => handleEditField('payment_date')}
                            sx={{ px: 0, py: 1.5 }}
                        >
                            <ListItemIcon>
                                <PaymentIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Payment date"
                                secondary={formatDate(specification.payment_date)}
                            />
                            <ArrowForwardIcon color="action" />
                        </ListItemButton>
                    </CardContent>
                </Card>

                {/* Action Sections */}
                <Card>
                    <CardContent sx={{ p: 0 }}>
                        <List>
                            {/* Deduction contributions */}
                            <ListItemButton
                                onClick={() => navigate(`/employer/salary-specification/${id}/deductions`)}
                            >
                                <ListItemIcon>
                                    <AccountBalanceIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Deduction contributions"
                                    secondary="Manage Deduction contributions"
                                    primaryTypographyProps={{
                                        fontWeight: 600
                                    }}
                                />
                                <ArrowForwardIcon color="action" />
                            </ListItemButton>
                            <Divider />

                            {/* Salary details */}
                            <ListItemButton
                                onClick={() => navigate(`/employer/salary-specification/${id}/salary-details`)}
                            >
                                <ListItemIcon>
                                    <DescriptionIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Salary details"
                                    secondary="Manage salary detail items"
                                    primaryTypographyProps={{
                                        fontWeight: 600
                                    }}
                                />
                                <ArrowForwardIcon color="action" />
                            </ListItemButton>
                            <Divider />

                            {/* Download */}
                            <ListItemButton onClick={handleDownload}>
                                <ListItemIcon>
                                    <DownloadIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Download"
                                    secondary="Download salary specification as PDF"
                                    primaryTypographyProps={{
                                        fontWeight: 600
                                    }}
                                />
                                <ArrowForwardIcon color="action" />
                            </ListItemButton>
                        </List>
                    </CardContent>
                </Card>

                {/* Edit Form Modal */}
                <SalarySpecificationForm
                    open={editFormOpen}
                    onClose={handleFormClose}
                    salarySpecificationId={id}
                    companyId={specification.company?.id}
                    onSaved={handleSpecificationSaved}
                />
            </Container>
        </section>
    );
}

export default SalarySpecificationDetailsPage;
